import { useNavigate, useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { authorizedFetch } from "@/lib/api"
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

interface RouteDetail {
  id: string
  code: string
  name: string
  km: number | null
  deletedAt: string | null
  originCityId: string
  originCityName: string
  originCityCode: string | null
  originStateName: string
  originCountryName: string
  targetCityId: string
  targetCityName: string
  targetCityCode: string | null
  targetStateName: string
  targetCountryName: string
  createdAt: string
  updatedAt: string
}

function fmtDate(v: string) {
  return new Date(v).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

function Field({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-sm" : "text-sm"}>
        {value ?? <span className="text-muted-foreground/50">—</span>}
      </dd>
    </div>
  )
}

export default function RouteDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: route, isLoading, isError } = useQuery({
    queryKey: ["geo", "route", id],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/geo/routes/${id}`)
      if (!res.ok) throw new Error("Ruta no encontrada")
      return res.json() as Promise<RouteDetail>
    },
    enabled: !!id,
  })

  if (isLoading) {
    return (
      <>
        <SiteHeader title="Ruta" />
        <div className="flex flex-1 items-center justify-center py-24 text-sm text-muted-foreground">
          Cargando...
        </div>
      </>
    )
  }

  if (isError || !route) {
    return (
      <>
        <SiteHeader title="Ruta no encontrada" />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24">
          <p className="text-sm text-muted-foreground">No se encontró la ruta.</p>
          <Button variant="outline" onClick={() => navigate("/geo/routes")}>
            <ArrowLeftIcon className="size-4 mr-2" /> Volver a rutas
          </Button>
        </div>
      </>
    )
  }

  return (
    <>
      <SiteHeader title={route.name} />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-2xl space-y-6">

          {/* Barra superior */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1 text-sm h-8 px-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <ArrowLeftIcon className="size-4" /> Volver
            </button>
            <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded uppercase tracking-wide">
              {route.code}
            </span>
            {route.km != null && (
              <Badge variant="secondary">{route.km} km</Badge>
            )}
            {route.deletedAt && (
              <Badge className="bg-destructive/10 text-destructive border-destructive/20">
                Eliminada
              </Badge>
            )}
          </div>

          {/* Recorrido */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Recorrido
            </h3>
            <div className="flex items-start gap-4">
              {/* Origen */}
              <div className="flex-1 rounded-md bg-muted/50 p-3 space-y-1">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Origen</p>
                <p className="font-medium text-sm">{route.originCityName}</p>
                {route.originCityCode && (
                  <span className="font-mono text-xs bg-background px-1.5 py-0.5 rounded border">
                    {route.originCityCode}
                  </span>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {route.originStateName} · {route.originCountryName}
                </p>
              </div>

              <div className="flex flex-col items-center justify-center pt-8 gap-1 shrink-0">
                <ArrowRightIcon className="size-5 text-muted-foreground" />
                {route.km != null && (
                  <span className="text-xs font-semibold text-muted-foreground">{route.km} km</span>
                )}
              </div>

              {/* Destino */}
              <div className="flex-1 rounded-md bg-muted/50 p-3 space-y-1">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Destino</p>
                <p className="font-medium text-sm">{route.targetCityName}</p>
                {route.targetCityCode && (
                  <span className="font-mono text-xs bg-background px-1.5 py-0.5 rounded border">
                    {route.targetCityCode}
                  </span>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {route.targetStateName} · {route.targetCountryName}
                </p>
              </div>
            </div>
          </div>

          {/* Identificación */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Identificación
            </h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Código" value={route.code} mono />
              <Field label="Nombre" value={route.name} />
              <Field label="Kilometraje" value={route.km != null ? `${route.km} km` : null} />
            </dl>
          </div>

          {/* Registro */}
          <div className="rounded-lg border p-5 space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Registro
            </h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Creado"      value={fmtDate(route.createdAt)} />
              <Field label="Actualizado" value={fmtDate(route.updatedAt)} />
              {route.deletedAt && (
                <Field label="Eliminada" value={fmtDate(route.deletedAt)} />
              )}
            </dl>
          </div>

        </div>
      </div>
    </>
  )
}
